import React from 'react';
import type { CarouselsWidgetData, ComparisonProduct } from '../../types/widgets';
import { useIsCompactVoiceLayout } from '../../hooks/useIsCompactVoiceLayout';

const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

function MiniCard({ product }: { product: ComparisonProduct }) {
  return (
    <div
      style={{
        background: 'var(--voice-widget-card-bg, #FFFFFF)',
        border: '1px solid var(--voice-widget-border, #E5E7EB)',
        borderRadius: 10,
        padding: '10px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        animation: 'voice-widget-fade-in 0.28s ease-out both',
        minWidth: 0,
      }}
    >
      {product.imageUrl && (
        <img
          src={product.imageUrl}
          alt={product.title}
          style={{
            width: '100%',
            height: 56,
            objectFit: 'contain',
            borderRadius: 6,
            background: 'var(--voice-widget-img-bg, #F9FAFB)',
          }}
        />
      )}
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--voice-widget-fg, #111827)',
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {product.title}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--voice-widget-price, #111827)', marginTop: 'auto' }}>
        {product.price}
      </div>
    </div>
  );
}

export interface CarouselsWidgetProps {
  data: CarouselsWidgetData;
}

export const CarouselsWidget: React.FC<CarouselsWidgetProps> = ({ data }) => {
  const sections = data.sections.slice(0, 4);
  const isCompact = useIsCompactVoiceLayout();

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 700,
        padding: '12px 12px 8px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        overflowY: 'auto',
        maxHeight: '100%',
      }}
    >
      <style>{WIDGET_KEYFRAMES}</style>
      {sections.map((section) => (
        <div key={section.title}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--voice-widget-muted, #6B7280)',
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              marginBottom: 8,
            }}
          >
            {section.title}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isCompact
                ? 'repeat(2, minmax(0, 1fr))'
                : `repeat(${Math.min(section.products.length, 4)}, 1fr)`,
              gap: 8,
            }}
          >
            {section.products.slice(0, isCompact ? 6 : 4).map((p) => (
              <MiniCard key={p.stacklineSku} product={p} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CarouselsWidget;
